import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { LangToggle } from "@/components/LangToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { SignOut, ChartLine } from "@phosphor-icons/react";
import { RowActions, ManagePanel, ArchivedPanel, AuditPanel } from "@/components/AdminActions";
const TABS = ["products", "contacts", "employees", "hq_areas", "targets", "settings", "approvals", "records", "archived", "audit"];
const RECORD_MODULES = ["visits", "expenses", "followups", "targets", "sessions", "change_requests"];

const ok = () => toast.success("Saved successfully");
const err = (e) => toast.error(e?.response?.data?.detail || "Save failed");
const confirmOff = (t) => window.confirm(t("confirm_deactivate"));

const Field = ({ label, ...p }) => (
  <label className="block">
    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
    <input {...p} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-200 px-3" />
  </label>
);

export default function Admin() {
  const { logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  return (
    <div className="max-w-5xl mx-auto min-h-screen bg-slate-50">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <h1 className="font-head font-bold text-lg text-slate-800">{t("administration")}</h1>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <LangToggle />
          <button onClick={() => nav("/manager")} data-testid="go-manager" className="p-2"><ChartLine size={20} /></button>
          <button onClick={logout} data-testid="logout-btn" className="p-2 text-red-500"><SignOut size={20} /></button>
        </div>
      </header>
      <div className="p-4 space-y-4">
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {TABS.map((tb) => (
            <button key={tb} data-testid={`admin-tab-${tb}`} onClick={() => setTab(tb)}
              className={`px-4 min-h-[40px] rounded-lg text-sm font-semibold capitalize whitespace-nowrap ${tab === tb ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
              {t(tb) === tb ? tb : t(tb)}
            </button>
          ))}
        </div>
        {tab === "products" && <Products />}
        {tab === "contacts" && <Contacts />}
        {tab === "employees" && <Employees />}
        {tab === "hq_areas" && <HqAreas />}
        {tab === "targets" && <Targets />}
        {tab === "settings" && <SettingsPanel />}
        {tab === "approvals" && <Approvals />}
        {tab === "records" && <RecordsTab />}
        {tab === "archived" && <ArchivedPanel />}
        {tab === "audit" && <AuditPanel />}
      </div>
    </div>
  );
}

function RecordsTab() {
  const { t } = useLang();
  const [module, setModule] = useState("visits");
  return (
    <div className="space-y-3">
      <select value={module} onChange={(e) => setModule(e.target.value)} data-testid="records-module-select"
        className="w-full sm:w-64 min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white capitalize">
        {RECORD_MODULES.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
      <ManagePanel module={module} />
      <p className="text-xs text-slate-400">{t("records")}</p>
    </div>
  );
}

const StatusPill = ({ active }) => (
  <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${active ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{active ? "Active" : "Inactive"}</span>
);

function Products() {
  const { t } = useLang();
  const empty = { name: "", product_code: "", pack_size: "", category: "", mrp: "", selling_price: "", gst: 12, image: "" };
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/products", { params: { include_inactive: true } }).then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (form.id) await api.put(`/products/${form.id}`, form);
      else await api.post("/products", form);
      setForm(null); ok(); load();
    } catch (e) { err(e); }
  };
  const toggle = async (p) => {
    if (p.active && !confirmOff(t)) return;
    try { await api.put(`/products/${p.id}`, { active: !p.active }); ok(); load(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3">
      {!form && <button onClick={() => setForm(empty)} data-testid="add-product-btn" className="min-h-[48px] px-5 rounded-lg bg-emerald-600 text-white font-semibold">+ {t("add_product")}</button>}
      {form && (
        <div className="rounded-xl bg-white border border-slate-200 p-4 grid sm:grid-cols-2 gap-3" data-testid="product-form">
          <Field label={t("name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" />
          <Field label={t("product_code")} value={form.product_code} onChange={(e) => setForm({ ...form, product_code: e.target.value })} data-testid="product-code" />
          <Field label="Pack Size" value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} />
          <Field label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          <Field label="MRP" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} data-testid="product-mrp" />
          <Field label="Selling Rate" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} data-testid="product-sp" />
          <Field label="GST %" value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value })} />
          <Field label="Image URL (optional)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={() => setForm(null)} className="flex-1 min-h-[44px] rounded-lg border border-slate-300 font-semibold" data-testid="product-cancel">{t("cancel")}</button>
            <button onClick={save} className="flex-1 min-h-[44px] rounded-lg bg-emerald-600 text-white font-semibold" data-testid="product-save">{t("save")}</button>
          </div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-3">
        {list.map((p) => (
          <div key={p.id} className={`rounded-xl bg-white border p-4 flex gap-3 ${p.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`admin-product-${p.id}`}>
            <div className="h-14 w-14 rounded-lg bg-slate-100 overflow-hidden shrink-0">{p.image && <img src={p.image} alt="" className="h-full w-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-slate-800 truncate">{p.name}</p>
                <StatusPill active={p.active} />
              </div>
              <p className="text-xs text-slate-500">{p.product_code ? `${p.product_code} · ` : ""}{p.pack_size}</p>
              <p className="text-sm font-semibold text-emerald-700 mt-1">{money(p.selling_price)} <span className="text-slate-400 line-through text-xs">{money(p.mrp)}</span> · {p.gst}%</p>
              <div className="flex gap-3 mt-2 text-xs font-semibold">
                <button onClick={() => setForm(p)} className="text-amber-600" data-testid={`edit-product-${p.id}`}>{t("edit")}</button>
                <button onClick={() => toggle(p)} className={p.active ? "text-red-600" : "text-emerald-600"} data-testid={`toggle-product-${p.id}`}>{p.active ? t("deactivate") : t("activate")}</button>
                <RowActions module="products" record={p} onDone={load} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const CFIELDS = {
  doctor: ["name", "speciality", "clinic", "mobile", "area"],
  retailer: ["name", "contact_person", "mobile", "area", "linked_stockist"],
  stockist: ["business_name", "contact_person", "mobile", "area", "gst_number"],
};
const CTYPE_MODULE = { doctor: "doctors", retailer: "retailers", stockist: "stockists" };

function Contacts() {
  const { t } = useLang();
  const [ctype, setCtype] = useState("doctor");
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get(`/contacts/${ctype}`, { params: { include_inactive: true, q } }).then(({ data }) => setList(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ctype]);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [q]);
  const nameOf = (c) => c.name || c.business_name;
  const save = async () => {
    try {
      if (form.id) await api.put(`/contacts/${ctype}/${form.id}`, form);
      else await api.post(`/contacts/${ctype}`, form);
      setForm(null); ok(); load();
    } catch (e) { err(e); }
  };
  const toggle = async (c) => {
    if (c.active && !confirmOff(t)) return;
    try { await api.put(`/contacts/${ctype}/${c.id}`, { active: !c.active }); ok(); load(); } catch (e) { err(e); }
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        {["doctor", "retailer", "stockist"].map((x) => (
          <button key={x} onClick={() => { setCtype(x); setForm(null); }} data-testid={`contacts-tab-${x}`}
            className={`px-3 min-h-[40px] rounded-lg text-sm font-semibold capitalize ${ctype === x ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{x}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="flex-1 min-h-[44px] rounded-lg border border-slate-200 px-3" data-testid="contacts-search" />
        {!form && <button onClick={() => setForm({})} className="px-4 rounded-lg bg-emerald-600 text-white font-semibold" data-testid="contacts-add">{t("add_new")}</button>}
      </div>
      {form && (
        <div className="rounded-xl bg-white border border-slate-200 p-4 grid sm:grid-cols-2 gap-3" data-testid="contact-form">
          {CFIELDS[ctype].map((f) => (
            <Field key={f} label={f.replace("_", " ")} value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`contact-${f}`} />
          ))}
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={() => setForm(null)} className="flex-1 min-h-[44px] rounded-lg border border-slate-300 font-semibold">{t("cancel")}</button>
            <button onClick={save} className="flex-1 min-h-[44px] rounded-lg bg-emerald-600 text-white font-semibold" data-testid="contact-save">{t("save")}</button>
          </div>
        </div>
      )}
      <div className="rounded-xl bg-white border border-slate-200 divide-y">
        {list.map((c) => (
          <div key={c.id} className={`flex items-center justify-between px-4 py-3 ${c.active ? "" : "opacity-60"}`} data-testid={`admin-contact-${c.id}`}>
            <div className="min-w-0">
              <p className="font-semibold text-slate-800 truncate">{nameOf(c)}</p>
              <p className="text-xs text-slate-500 truncate">{[c.speciality, c.area, c.mobile].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <StatusPill active={c.active} />
              <button onClick={() => setForm(c)} className="text-xs font-semibold text-amber-600">{t("edit")}</button>
              <button onClick={() => toggle(c)} className={`text-xs font-semibold ${c.active ? "text-red-600" : "text-emerald-600"}`} data-testid={`toggle-contact-${c.id}`}>{c.active ? t("deactivate") : t("activate")}</button>
              <RowActions module={CTYPE_MODULE[ctype]} record={c} onDone={load} />
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="text-center text-slate-400 py-8">{t("no_data")}</p>}
      </div>
    </div>
  );
}

function Employees() {
  const { t } = useLang();
  const empty = { role: "mr", pin: "1234", active: true };
  const [list, setList] = useState([]);
  const [areas, setAreas] = useState([]);
  const [hqs, setHqs] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/users").then(({ data }) => setList(data));
  useEffect(() => {
    load();
    api.get("/areas").then(({ data }) => setAreas(data));
    api.get("/headquarters").then(({ data }) => setHqs(data));
  }, []);
  const managers = list.filter((u) => u.role === "manager");
  const save = async () => {
    try {
      if (form.id) await api.put(`/users/${form.id}`, form);
      else await api.post("/users", form);
      setForm(null); ok(); load();
    } catch (e) { err(e); }
  };
  const toggle = async (u) => {
    if (u.active !== false && !confirmOff(t)) return;
    try { await api.put(`/users/${u.id}`, { active: u.active === false }); ok(); load(); } catch (e) { err(e); }
  };
  const resetPin = async (u) => {
    const p = prompt("New 4-digit PIN", "1234");
    if (p && /^\d{4}$/.test(p)) { await api.put(`/users/${u.id}`, { pin: p }); ok(); }
  };
  return (
    <div className="space-y-3">
      {!form && <button onClick={() => setForm(empty)} className="min-h-[48px] px-5 rounded-lg bg-emerald-600 text-white font-semibold" data-testid="add-employee-btn">+ {t("add_new")}</button>}
      {form && (
        <div className="rounded-xl bg-white border border-slate-200 p-4 grid sm:grid-cols-2 gap-3" data-testid="employee-form">
          <Field label={t("name")} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name" />
          <Field label={t("employee_id")} value={form.employee_id || ""} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="emp-id" />
          <Field label={t("mobile")} value={form.mobile || ""} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="emp-mobile" />
          <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white" data-testid="emp-role">
              <option value="mr">Medical Representative</option><option value="manager">Manager</option><option value="admin">Administrator</option>
            </select>
          </label>
          <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reporting Manager</span>
            <select value={form.manager_id || ""} onChange={(e) => setForm({ ...form, manager_id: e.target.value })} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white">
              <option value="">--</option>{managers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("headquarters")}</span>
            <select value={form.headquarters || ""} onChange={(e) => setForm({ ...form, headquarters: e.target.value })} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white">
              <option value="">--</option>{hqs.map((h) => <option key={h.id} value={h.name}>{h.name}</option>)}
            </select>
          </label>
          <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("working_area")}</span>
            <select value={(form.working_areas || [])[0] || ""} onChange={(e) => setForm({ ...form, working_areas: [e.target.value] })} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white">
              <option value="">--</option>{areas.map((a) => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </label>
          <Field label="Joining Date" type="date" value={form.joining_date || ""} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} />
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={() => setForm(null)} className="flex-1 min-h-[44px] rounded-lg border border-slate-300 font-semibold">{t("cancel")}</button>
            <button onClick={save} className="flex-1 min-h-[44px] rounded-lg bg-emerald-600 text-white font-semibold" data-testid="emp-save">{t("save")}</button>
          </div>
        </div>
      )}
      <div className="rounded-xl bg-white border border-slate-200 divide-y">
        {list.map((u) => (
          <div key={u.id} className={`flex items-center justify-between px-4 py-3 ${u.active === false ? "opacity-60" : ""}`} data-testid={`admin-user-${u.employee_id}`}>
            <div><p className="font-semibold text-slate-800">{u.name}</p><p className="text-xs text-slate-500">{u.employee_id} · {u.mobile} · {u.role}</p></div>
            <div className="flex items-center gap-3 shrink-0 text-xs font-semibold">
              <StatusPill active={u.active !== false} />
              <button onClick={() => setForm(u)} className="text-amber-600">{t("edit")}</button>
              <button onClick={() => resetPin(u)} className="text-blue-600" data-testid={`reset-pin-${u.employee_id}`}>{t("reset_pin_btn")}</button>
              <button onClick={() => toggle(u)} className={u.active === false ? "text-emerald-600" : "text-red-600"} data-testid={`toggle-user-${u.employee_id}`}>{u.active === false ? t("activate") : t("deactivate")}</button>
              <RowActions module="users" record={u} onDone={load} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HqAreas() {
  const { t } = useLang();
  const [hqs, setHqs] = useState([]);
  const [areas, setAreas] = useState([]);
  const [hqName, setHqName] = useState("");
  const [areaName, setAreaName] = useState("");
  const [areaHq, setAreaHq] = useState("");
  const load = () => {
    api.get("/headquarters").then(({ data }) => setHqs(data));
    api.get("/areas").then(({ data }) => setAreas(data));
  };
  useEffect(() => { load(); }, []);
  const addHq = async () => { if (!hqName) return; await api.post("/headquarters", { name: hqName }); setHqName(""); ok(); load(); };
  const addArea = async () => { if (!areaName) return; await api.post("/areas", { name: areaName, headquarters: areaHq }); setAreaName(""); ok(); load(); };
  const toggleArea = async (a) => { if (a.active && !confirmOff(t)) return; try { await api.put(`/areas/${a.id}`, { active: !a.active }); load(); } catch (e) { err(e); } };
  const toggleHq = async (h) => { if (h.active && !confirmOff(t)) return; try { await api.put(`/headquarters/${h.id}`, { active: !h.active }); load(); } catch (e) { err(e); } };
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="space-y-3">
        <h3 className="font-head font-semibold text-slate-800">{t("headquarters")}</h3>
        <div className="flex gap-2">
          <input value={hqName} onChange={(e) => setHqName(e.target.value)} placeholder="New HQ" className="flex-1 min-h-[44px] rounded-lg border border-slate-200 px-3" data-testid="hq-name" />
          <button onClick={addHq} className="px-4 rounded-lg bg-emerald-600 text-white font-semibold" data-testid="hq-add">{t("save")}</button>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 divide-y">
          {hqs.map((h) => (
            <div key={h.id} className={`flex items-center justify-between px-4 py-3 ${h.active ? "" : "opacity-60"}`} data-testid={`admin-hq-${h.id}`}>
              <span className="font-semibold text-slate-700">{h.name}</span>
              <div className="flex items-center gap-3">
                <button onClick={() => toggleHq(h)} className={`text-xs font-semibold ${h.active ? "text-red-600" : "text-emerald-600"}`}>{h.active ? t("deactivate") : t("activate")}</button>
                <RowActions module="headquarters" record={h} onDone={load} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-3">
        <h3 className="font-head font-semibold text-slate-800">{t("areas")}</h3>
        <div className="space-y-2">
          <input value={areaName} onChange={(e) => setAreaName(e.target.value)} placeholder="New area" className="w-full min-h-[44px] rounded-lg border border-slate-200 px-3" data-testid="area-name" />
          <div className="flex gap-2">
            <select value={areaHq} onChange={(e) => setAreaHq(e.target.value)} className="flex-1 min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white">
              <option value="">Link HQ</option>{hqs.map((h) => <option key={h.id} value={h.name}>{h.name}</option>)}
            </select>
            <button onClick={addArea} className="px-4 rounded-lg bg-emerald-600 text-white font-semibold" data-testid="area-create">{t("save")}</button>
          </div>
        </div>
        <div className="rounded-xl bg-white border border-slate-200 divide-y">
          {areas.map((a) => (
            <div key={a.id} className={`flex items-center justify-between px-4 py-3 ${a.active ? "" : "opacity-60"}`} data-testid={`admin-area-${a.id}`}>
              <span className="font-semibold text-slate-700">{a.name} <span className="text-xs text-slate-400">{a.headquarters}</span></span>
              <div className="flex items-center gap-3">
                <button onClick={() => toggleArea(a)} className={`text-xs font-semibold ${a.active ? "text-red-600" : "text-emerald-600"}`} data-testid={`toggle-area-${a.id}`}>{a.active ? t("deactivate") : t("activate")}</button>
                <RowActions module="areas" record={a} onDone={load} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Targets() {
  const { t } = useLang();
  const [mrs, setMrs] = useState([]);
  const [uid, setUid] = useState("");
  const [form, setForm] = useState({ daily_doctor: 12, daily_retailer: 5, daily_stockist: 2, monthly_order: 0, monthly_collection: 0, start_date: "", end_date: "" });
  useEffect(() => { api.get("/users").then(({ data }) => setMrs(data.filter((u) => u.role === "mr"))); }, []);
  useEffect(() => {
    if (!uid) return;
    api.get("/targets", { params: { user_id: uid } }).then(({ data }) => { if (data[0]) setForm({ ...form, ...data[0] }); });
    // eslint-disable-next-line
  }, [uid]);
  const save = async () => {
    if (!uid) { toast.error("Select an employee"); return; }
    try { await api.post("/targets", { ...form, user_id: uid }); ok(); } catch (e) { err(e); }
  };
  const F = ({ k, label }) => (
    <Field label={label} type="number" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`target-${k}`} />
  );
  return (
    <div className="max-w-lg rounded-xl bg-white border border-slate-200 p-4 space-y-3">
      <label className="block"><span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Employee</span>
        <select value={uid} onChange={(e) => setUid(e.target.value)} className="mt-1 w-full min-h-[44px] rounded-lg border border-slate-200 px-3 bg-white" data-testid="target-employee">
          <option value="">-- Select MR --</option>{mrs.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.employee_id})</option>)}
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <F k="daily_doctor" label="Daily Doctor Calls" />
        <F k="daily_retailer" label="Daily Retailer Calls" />
        <F k="daily_stockist" label="Daily Stockist Calls" />
        <F k="monthly_order" label="Monthly Order ₹" />
        <F k="monthly_collection" label="Monthly Collection ₹" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start Date" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        <Field label="End Date" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
      </div>
      <button onClick={save} className="min-h-[48px] px-5 rounded-lg bg-emerald-600 text-white font-semibold w-full" data-testid="target-save">{t("save")}</button>
    </div>
  );
}

const OPT_TYPES = [["speciality", "Doctor Specialities"], ["expense_category", "Expense Categories"], ["payment_method", "Payment Methods"], ["followup_option", "Follow-up Options"]];

function OptionList({ type, title }) {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [label, setLabel] = useState("");
  const load = () => api.get("/options", { params: { type, include_inactive: true } }).then(({ data }) => setList(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = async () => { if (!label) return; await api.post("/options", { type, label }); setLabel(""); ok(); load(); };
  const toggle = async (o) => { if (o.active && !confirmOff(t)) return; try { await api.put(`/options/${o.id}`, { active: !o.active }); load(); } catch (e) { err(e); } };
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-4 space-y-2">
      <h4 className="font-semibold text-slate-800">{title}</h4>
      <div className="flex gap-2">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("add_new")} className="flex-1 min-h-[40px] rounded-lg border border-slate-200 px-3" data-testid={`opt-input-${type}`} />
        <button onClick={add} className="px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold" data-testid={`opt-add-${type}`}>+</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((o) => (
          <div key={o.id} className="flex items-center gap-1 border border-slate-200 rounded-lg px-2 py-1" data-testid={`opt-row-${type}-${o.id}`}>
            <button onClick={() => toggle(o)} data-testid={`opt-${type}-${o.id}`}
              className={`text-sm font-medium ${o.active ? "text-emerald-700" : "text-slate-400 line-through"}`}>
              {o.label}
            </button>
            <RowActions module="options" record={o} onDone={load} />
          </div>
        ))}
      </div>
    </div>
  );
}

function SettingsPanel() {
  const { t } = useLang();
  const [val, setVal] = useState(720);
  useEffect(() => { api.get("/settings").then(({ data }) => setVal(data.max_backdate_minutes || 720)); }, []);
  const save = async () => { await api.put("/settings", { max_backdate_minutes: Number(val) }); ok(); };
  return (
    <div className="space-y-4">
      <div className="max-w-md rounded-xl bg-white border border-slate-200 p-4 space-y-3">
        <label className="text-sm font-semibold text-slate-700">{t("max_backdate")}</label>
        <input type="number" value={val} onChange={(e) => setVal(e.target.value)} data-testid="settings-backdate" className="w-full min-h-[48px] rounded-lg border border-slate-200 px-3" />
        <button onClick={save} data-testid="settings-save" className="min-h-[44px] px-5 rounded-lg bg-emerald-600 text-white font-semibold">{t("save")}</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {OPT_TYPES.map(([type, title]) => <OptionList key={type} type={type} title={title} />)}
      </div>
    </div>
  );
}

function Approvals() {
  const [list, setList] = useState([]);
  const load = () => api.get("/change-requests").then(({ data }) => setList(data));
  useEffect(() => { load(); }, []);
  const decide = async (id, d) => { await api.post(`/change-requests/${id}/${d}`); toast.success(d); load(); };
  return (
    <div className="space-y-3">
      {list.map((r) => (
        <div key={r.id} className="rounded-xl bg-white border border-slate-200 p-4" data-testid={`approval-${r.id}`}>
          <p className="text-sm text-slate-500">{r.ctype} · by {r.requested_by}</p>
          <pre className="text-xs bg-slate-50 rounded p-2 my-2 overflow-x-auto">{JSON.stringify(r.changes, null, 1)}</pre>
          <div className="flex gap-2">
            <button onClick={() => decide(r.id, "approve")} className="flex-1 min-h-[40px] rounded bg-emerald-600 text-white text-sm font-semibold" data-testid={`approve-${r.id}`}>Approve</button>
            <button onClick={() => decide(r.id, "reject")} className="flex-1 min-h-[40px] rounded bg-red-500 text-white text-sm font-semibold" data-testid={`reject-${r.id}`}>Reject</button>
          </div>
        </div>
      ))}
      {list.length === 0 && <p className="text-center text-slate-400 py-10">No pending approvals</p>}
    </div>
  );
}
